import { SectionCard } from '@/components/SectionCard'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { ItemSummaryStats } from '@/features/items/components/ItemSummaryStats'
import { useConfirm } from '@/hooks/useConfirm'
import { getMutationOptions } from '@/utils/mutation'
import { Building2, Edit2, MapPin, Monitor, Plus, Trash2, Users } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useDeleteDepartment, useGetDepartments } from '../hooks/useDepartment'
import { DepartmentDialog } from './DepartmentDialog'

export function DepartmentsView() {
  const { t } = useTranslation()
  const confirm = useConfirm()
  const { isAdmin } = useAuth()
  const { data: departments = [], isLoading: loadingDepartment } = useGetDepartments()
  const deleteDepartment = useDeleteDepartment()

  const navigate = useNavigate()

  const [selectedDeptId, setSelectedDeptId] = useState<string | null>(null)
  const [departmentOpen, setDepartmentOpen] = useState(false)

  const canManage = isAdmin

  const handleDeleteDepartment = async (departmentId: string) => {
    const name = departments.find(i => i.id === departmentId)?.name
    const confirmed = await confirm({
      variant: 'destructive',
      title: t('common.deleteConfirmTitle'),
      description: t('department.confirmDeleteDescription', { name }),
    })

    if (confirmed) {
      const options = getMutationOptions({
        successMessage: t('department.toast.deleteSuccess'),
      })
      deleteDepartment.mutate(departmentId, options)
    }
  }

  return (
    <div className="flex flex-col gap-4">
      {canManage && (
        <div className={'flex justify-end'}>
          <Button
            data-testid="departments-create-button"
            size="sm"
            className="h-8 text-xs"
            onClick={() => {
              setSelectedDeptId(null)
              setDepartmentOpen(true)
            }}
          >
            <Plus className="size-3" />
            {t('department.add')}
          </Button>
        </div>
      )}

      <div data-testid="departments-list" className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {departments.map(dept => (
          <SectionCard
            data-testid="department-card"
            data-department-id={dept.id}
            key={dept.id}
            onClick={() => navigate(`/departments/${dept.id}/`)}
            cardClass="cursor-pointer border-border/50 bg-card transition-all hover:border-primary/30"
            headerClass="pb-0"
            header={
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <Building2 className="size-4 shrink-0 text-primary" />
                  <span data-testid="department-card-name" className="text-sm font-semibold text-foreground break-words line-clamp-2">{dept.name}</span>
                </div>

                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <MapPin className="size-3 shrink-0" />
                    <span className="break-words line-clamp-2">
                      {t('common.floor')} {dept.floorName}
                    </span>
                  </span>
                  <span className="flex items-center gap-1">
                    <Users className="size-3 shrink-0" />
                    <span className="break-words line-clamp-2">{dept.headName}</span>
                  </span>
                </div>
              </div>
            }
            right={
              <div className="flex flex-col gap-1">
                <div className="flex gap-1">
                  {canManage && (
                    <Button
                      data-testid="department-card-edit"
                      variant={'outline'}
                      size="sm"
                      className="h-6"
                      onClick={e => {
                        e.stopPropagation()
                        setSelectedDeptId(dept.id)
                        setDepartmentOpen(true)
                      }}
                    >
                      <Edit2 className="size-3" />
                    </Button>
                  )}
                  <Badge variant="outline" className="text-[10px] text-muted-foreground">
                    <Monitor className="mr-1 size-3" />
                    {dept?.itemSummary?.total}
                  </Badge>
                </div>

                {canManage && (
                  <div className="flex justify-end">
                    <Button
                      data-testid="department-card-delete"
                      variant={'outline'}
                      size="sm"
                      className="h-6 border-destructive/30 text-destructive hover:text-destructive hover:bg-destructive/10"
                      onClick={e => {
                        e.stopPropagation()
                        handleDeleteDepartment(dept.id)
                      }}
                    >
                      <Trash2 className="size-3" />
                    </Button>
                  </div>
                )}
              </div>
            }
          >
            <ItemSummaryStats summary={dept?.itemSummary}></ItemSummaryStats>
          </SectionCard>
        ))}
      </div>

      <DepartmentDialog
        open={departmentOpen}
        onOpenChange={setDepartmentOpen}
        departmentId={selectedDeptId ?? undefined}
      ></DepartmentDialog>
    </div>
  )
}
